import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardBody, Row, Col, Container } from "reactstrap";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import axiosInstance from "../../Request";
import { URL } from "../../api";

const inr = (v) =>
  "₹" +
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const inrShort = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 10000000) return "₹" + (n / 10000000).toFixed(2) + "Cr";
  if (Math.abs(n) >= 100000) return "₹" + (n / 100000).toFixed(2) + "L";
  if (Math.abs(n) >= 1000) return "₹" + (n / 1000).toFixed(1) + "K";
  return "₹" + n.toFixed(0);
};

const KPI_CARDS = [
  { key: "totalLoanAmount", label: "Total Loan Amount", color: "#5e72e4", money: true },
  { key: "totalCollection", label: "Total Collection", color: "#2dce89", money: true },
  { key: "totalOutstanding", label: "Total Outstanding", color: "#fb6340", money: true },
  { key: "overdueAmount", label: "Overdue", color: "#f5365c", money: true },
  { key: "activeLoans", label: "Active Loans", color: "#11cdef", money: false },
  { key: "collectionEfficiency", label: "Collection Efficiency", color: "#8965e0", pct: true },
];

const PIE_COLORS = ["#5e72e4", "#fb6340", "#f5365c"];

const StatCard = ({ label, value, color }) => (
  <Card className="card-stats mb-4 shadow">
    <CardBody>
      <Row className="align-items-center">
        <div className="col">
          <span
            className="h6 font-weight-bold text-muted text-uppercase mb-0 d-block"
            style={{ letterSpacing: "0.04em" }}
          >
            {label}
          </span>
          <span className="h2 font-weight-bold mb-0" style={{ color }}>
            {value}
          </span>
        </div>
      </Row>
    </CardBody>
  </Card>
);

const ChartCard = ({ title, children }) => (
  <Card className="shadow mb-4">
    <CardHeader className="bg-white border-0">
      <h3 className="mb-0">{title}</h3>
    </CardHeader>
    <CardBody>
      <div style={{ width: "100%", height: 320 }}>{children}</div>
    </CardBody>
  </Card>
);

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(URL.NBFC_ANALYTICS_SUMMARY);
        setData(res.data);
        setError(null);
      } catch (e) {
        console.error("Error fetching analytics summary:", e);
        setError("Could not load analytics data.");
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  const kpi = data?.kpi || {};
  const monthly = Array.isArray(data?.monthly) ? data.monthly : [];
  const breakdown = data?.outstandingBreakdown || {};
  const pieData = [
    { name: "Principal", value: Number(breakdown.principal || 0) },
    { name: "Interest", value: Number(breakdown.interest || 0) },
    { name: "Penal", value: Number(breakdown.penal || 0) },
  ].filter((d) => d.value > 0);

  const kpiValue = (c) => {
    const raw = kpi[c.key];
    if (c.pct) return `${Number(raw || 0).toFixed(2)}%`;
    if (c.money) return inr(raw);
    return Number(raw || 0).toLocaleString("en-IN");
  };

  return (
    <Container fluid className="pt-4">
      <Card className="bg-gradient-default shadow mb-4">
        <CardBody>
          <h6 className="text-uppercase text-light ls-1 mb-1">NBFC · Credit-Line Book</h6>
          <h2 className="text-white mb-0">Portfolio Analytics</h2>
        </CardBody>
      </Card>

      {loading && <div className="text-center text-muted py-5">Loading analytics…</div>}
      {error && !loading && <div className="text-center text-danger py-5">{error}</div>}

      {!loading && !error && (
        <>
          {/* KPI cards */}
          <Row>
            {KPI_CARDS.map((c) => (
              <Col key={c.key} xl="4" md="6">
                <StatCard label={c.label} value={kpiValue(c)} color={c.color} />
              </Col>
            ))}
          </Row>

          {/* Disbursed vs Collected trend */}
          <Row>
            <Col xl="8">
              <ChartCard title="Disbursed vs Collected (monthly)">
                <ResponsiveContainer>
                  <BarChart data={monthly} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e9ecef" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={inrShort} tick={{ fontSize: 12 }} width={70} />
                    <Tooltip formatter={(v) => inr(v)} />
                    <Legend />
                    <Bar dataKey="disbursed" name="Disbursed" fill="#5e72e4" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="collected" name="Collected" fill="#2dce89" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Col>

            {/* Outstanding breakdown */}
            <Col xl="4">
              <ChartCard title="Outstanding Breakdown">
                {pieData.length === 0 ? (
                  <div className="text-center text-muted pt-5">No outstanding balances</div>
                ) : (
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        innerRadius={55}
                        paddingAngle={2}
                        label={(d) => d.name}
                      >
                        {pieData.map((entry, i) => (
                          <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => inr(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Col>
          </Row>

          {/* Collection efficiency */}
          <Row>
            <Col xl="12">
              <ChartCard title="Collection Efficiency (%) by month">
                <ResponsiveContainer>
                  <LineChart data={monthly} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e9ecef" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 12 }} width={50} />
                    <Tooltip formatter={(v) => `${Number(v).toFixed(2)}%`} />
                    <Line
                      type="monotone"
                      dataKey="efficiency"
                      name="Efficiency"
                      stroke="#8965e0"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default Analytics;
